import { type ReactNode, useState } from 'react';
import { cn } from '@v2/utils/cn';

export type PodiumRow = {
    key: string;
    position: number;
    name: string;
    avatarUrl?: string | null;
    /** Valor principal (ex.: "R$ 263.848,28" ou "12 exceções"). */
    value: string;
    /** Linha curta abaixo do nome no pódio. */
    meta?: string;
    /** Linha de detalhe na lista. */
    detail?: string;
    /** 0..1 — barra proporcional ao 1º colocado. */
    ratio: number;
    /** Conteúdo extra à direita do detalhe (ex.: mistura de criticidade). */
    trailing?: ReactNode;
};

const MEDALS = ['🥇', '🥈', '🥉'];

function initials(name: string) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function Avatar({ row, size }: { row: PodiumRow; size: 'lg' | 'sm' }) {
    const [broken, setBroken] = useState(false);
    const box = size === 'lg' ? 'size-16 text-lg' : 'size-9 text-xs';

    return row.avatarUrl && !broken ? (
        <img src={row.avatarUrl} alt={row.name} onError={() => setBroken(true)} className={cn(box, 'rounded-full object-cover ring-2 ring-white dark:ring-zinc-900')} />
    ) : (
        <span className={cn(box, 'flex items-center justify-center rounded-full bg-primary/20 font-black text-primary-dark ring-2 ring-white dark:ring-zinc-900')}>
            {initials(row.name)}
        </span>
    );
}

/**
 * Ranking gamificado: pódio (2º · 1º · 3º) com medalhas e lista do 1º ao último.
 */
export default function SellerPodium({
    title,
    hint,
    rows,
    onPick,
    valueClass = 'text-red-700 dark:text-red-400',
    barClass = 'bg-red-500',
    headerRight,
}: {
    title: string;
    hint?: string;
    rows: PodiumRow[];
    onPick?: (row: PodiumRow) => void;
    valueClass?: string;
    barClass?: string;
    headerRight?: ReactNode;
}) {
    if (rows.length === 0) return null;

    const podium = rows.slice(0, 3);
    const podiumOrder = [podium[1], podium[0], podium[2]].filter(Boolean) as PodiumRow[];
    const heights: Record<number, string> = { 1: 'h-28', 2: 'h-20', 3: 'h-14' };
    const podiumColor: Record<number, string> = {
        1: 'from-primary to-primary-dark text-white',
        2: 'from-zinc-300 to-zinc-400 text-zinc-900',
        3: 'from-orange-300 to-orange-500 text-white',
    };

    return (
        <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
                <div>
                    <p className="text-sm font-bold text-gray-900 dark:text-zinc-50">{title}</p>
                    {hint && <p className="text-xs text-gray-500">{hint}</p>}
                </div>
                {headerRight}
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                <div className="flex items-end justify-center gap-3">
                    {podiumOrder.map((row) => (
                        <button key={row.key} type="button" onClick={() => onPick?.(row)} className="flex w-32 flex-col items-center text-center">
                            <span className="text-2xl">{MEDALS[row.position - 1]}</span>
                            <Avatar row={row} size="lg" />
                            <span className="mt-1 line-clamp-2 text-xs font-bold text-gray-900 dark:text-zinc-50">{row.name}</span>
                            <span className={cn('text-sm font-black tabular-nums', valueClass)}>{row.value}</span>
                            {row.meta && <span className="text-[10px] text-gray-500">{row.meta}</span>}
                            <div className={cn('mt-2 flex w-full items-start justify-center rounded-t-lg bg-gradient-to-b pt-1 text-lg font-black', heights[row.position], podiumColor[row.position])}>
                                {row.position}º
                            </div>
                        </button>
                    ))}
                </div>

                <ol className="space-y-2">
                    {rows.map((row) => (
                        <li key={row.key}>
                            <button type="button" onClick={() => onPick?.(row)} className="flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-zinc-800/50">
                                <span className={cn(
                                    'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-black',
                                    row.position <= 3 ? 'bg-gray-900 text-white dark:bg-white dark:text-zinc-900' : 'bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-300',
                                )}
                                >
                                    {row.position}
                                </span>
                                <Avatar row={row} size="sm" />
                                <span className="min-w-0 flex-1">
                                    <span className="flex items-center justify-between gap-2">
                                        <span className="truncate text-xs font-semibold text-gray-900 dark:text-zinc-100">{row.name}</span>
                                        <span className="shrink-0 text-xs font-black tabular-nums">{row.value}</span>
                                    </span>
                                    <span className="mt-1 block h-1.5 rounded-full bg-gray-100 dark:bg-zinc-800">
                                        <span className={cn('block h-1.5 rounded-full', barClass)} style={{ width: `${Math.max(0, Math.min(1, row.ratio)) * 100}%` }} />
                                    </span>
                                    {(row.detail || row.trailing) && (
                                        <span className="mt-1 flex items-center justify-between gap-2 text-[10px] text-gray-500">
                                            <span>{row.detail}</span>
                                            {row.trailing && <span className="w-20">{row.trailing}</span>}
                                        </span>
                                    )}
                                </span>
                            </button>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    );
}
